import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBank,
  faCheck,
  faExchange,
  faFlag,
  faWallet,
  faXmark,
  faQrcode,
  faMobile,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp, faTelegramPlane } from "@fortawesome/free-brands-svg-icons"
import { withAppHOC } from "../../hoc";
import { StyledContentContainer, StyledIconedText } from "../../components/styled";
import { MessageBox } from "../../components/FormElements";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import { formatCurrency,formatCurrency4Decimal, getAuthData } from "../../utils";
import { Link } from "react-router-dom";
import { TableBuilder } from "../../components/TableElements";
import { Image } from "../../components/Image";
import moment from "moment";
import Slider from "../../components/Slider/Slider";

// -------------------- Styled Components --------------------

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const StyledDashboardContainer = styled.section`
  padding: 20px;
`;

const StyledHeading = styled.h1`
  font-size: 24px;
  font-weight: normal;
  color: #000000;
  margin-bottom: 20px;
`;

const StyledStatsContainerOuter = styled.section`
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  @media (max-width: 600px) {
    display: block;
  }
`;

const StyledStatsContainer = styled.section`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  a {
    text-decoration: none;
  }
  @media (max-width: 600px) {
    display: block;
  }
`;

const StyledStatsSection = styled.section`
  width: 300px;
  height: 100px;
  background: ${(props) => props.bg};
  border-radius: 12px;
  display: grid;
  grid-template-columns: max-content auto;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    animation: ${pulse} 1s infinite;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.2);
  }
  @media (max-width: 600px) {
    width: 100%;
    margin-bottom: 15px;
  }
`;

const StyledStatsIcon = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0px 15px;
  svg {
    width: 30px;
    height: 30px;
    color: #ffffff;
    border: 5px solid #ffffff;
    padding: 15px;
    border-radius: 50%;
  }
`;

const StyledStatsCounter = styled.section`
  width: 100%;
  height: 100%;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledStatsHeading = styled.p`
  font-size: 14px;
  margin: 0px;
  padding: 0 15px;
  color: #e0e0e0;
  text-transform: capitalize;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledStatsCount = styled.h3`
  font-size: 22px;
  margin: 0px;
  padding: 5px 15px 0px 15px;
  color: #ffffff;
`;

const StyledFlexBoxesContainer = styled.section`
  display: flex;
  column-gap: 30px;
  flex-wrap: wrap;
  padding: 20px;
  @media (max-width: 600px) {
    display: block;
  }
`;

const StyledTableContainer = styled.section`
  margin-bottom: 20px;
  flex: 1;
`;

const StyledTableContainerIn = styled.section`
 overflow: hidden;

  table {
    width: 100%;
    border-collapse: collapse;
    color: #f0f0f0;
  }

  thead {
    background: #7f7f7fff;
    color: #ffffff;
  }

  tbody tr:nth-child(even) {
    background: #ffffff;
  }

  tbody tr:nth-child(odd) {
    background: #f9f9f9ff;
  }

  td, th {
    padding: 10px 15px;
    text-align: left;
    vertical-align: middle;
  }

  tfoot {
    background: #656565ff;
    font-weight: bold;
    color: #ffffff;
  }

  tfoot td {
    padding: 10px 15px;
    text-align: left;
    vertical-align: middle;
  }
`;

// -------------------- Modal --------------------
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

// -------------------- MAIN DASHBOARD --------------------
const Dashboard = (props) => {
  const { userProfile } = getAuthData();
  const { isGSTSeller } = userProfile;

  const [isQRVisible, setQRVisible] = useState(false);
  const [qrImage, setQRImage] = useState("/telegrambot.jpeg");

  const [supportNumber1, setSupportNumber1] = useState("");
  const [supportNumber2, setSupportNumber2] = useState("");

  // Fetch support numbers
  const fetchFirmDetails = async () => {
    const response = await API.get("/meta/firm-details");
    const data = response.data?.data;

    const supportNumbers = data?.supportNumber
      ?.split(",")
      .map((n) => n.trim())
      .filter(Boolean);

    return supportNumbers?.slice(0, 2);
  };

  useEffect(() => {
    const loadSupport = async () => {
      const [num1, num2] = await fetchFirmDetails();
      setSupportNumber1(num1 || "");
      setSupportNumber2(num2 || "");
    };
    loadSupport();
  }, []);

  // Summary fetcher
  const fetchSummary = async (type) => {
    const response = await API.get(
      `/reports/operator-daybook?reportLocation=dashboard&reportType=${type}&marginType=${isGSTSeller === "Yes" ? "GST" : "Non-GST"
      }&fromDate=${moment().format("YYYY-MM-DD")}&toDate=${moment().format("YYYY-MM-DD")}`
    );
    return response.data;
  };

  // Queries
  const { data: saleSummary, isLoading: saleIsLoading } = useQuery({
    queryKey: ["sale_summary"],
    queryFn: () => fetchSummary("Seller"),
  });

  const { data: purchaseSummary, isLoading: purchaseIsLoading } = useQuery({
    queryKey: ["purchase_summary"],
    queryFn: () => fetchSummary("Buyer"),
  });

  const { data: dashboardData, error } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const res = await API.get("/dashboard");
      return res?.data?.data;
    },
  });

  if (error) {
    return (
      <StyledContentContainer>
        <MessageBox status={RESPONSE_STATUSES.FAILED} message={error.message} />
      </StyledContentContainer>
    );
  }

  const stats = dashboardData?.[0] || {};
  const { appData } = props;

  const cardGradients = [
  "linear-gradient(135deg, #0f172a, #1e293b)",
  "linear-gradient(135deg, #111827, #374151)",
  "linear-gradient(135deg, #1c1c1c, #2d2d2d)",
  "linear-gradient(135deg, #0b1320, #111827)",
  "linear-gradient(135deg, #0f2027, #203a43)",
];


  // Normalizes phone-like strings into a 10-digit local number when possible
  const truncate91 = (text = "") => {
    const s = String(text || "").trim();

    // Quick return for empty
    if (!s) return "";

    // Remove everything that's not a digit
    let digits = s.replace(/\D/g, "");

    // If digits start with country code 91 and we have more than 10 digits, strip one leading 91
    if (digits.length > 10 && digits.startsWith("91")) {
      digits = digits.replace(/^91/, "");
    }

    // If still longer than 10, keep the last 10 digits (fallback)
    if (digits.length > 10) {
      digits = digits.slice(-10);
    }

    // Return digits as-is (will be <= 10 here)
    return digits;
  };


  const renderCard = (icon, count, heading, link, index, qrSrc) => {
    if (qrSrc) {
      return (
        <StyledStatsSection
          key={index}
          bg={cardGradients[index % cardGradients.length]}
          onClick={() => {
            setQRImage(qrSrc);
            setQRVisible(true);
          }}
        >
          <StyledStatsIcon>
            <FontAwesomeIcon icon={icon} />
          </StyledStatsIcon>
          <StyledStatsCounter>
            <StyledStatsCount>Scan QR</StyledStatsCount>
            <StyledStatsHeading>{heading}</StyledStatsHeading>
          </StyledStatsCounter>
        </StyledStatsSection>
      );
    }

    return (
      <Link to={link} key={index}>
        <StyledStatsSection bg={cardGradients[index % cardGradients.length]}>
          <StyledStatsIcon>
            <FontAwesomeIcon icon={icon} />
          </StyledStatsIcon>
          <StyledStatsCounter>
            <StyledStatsCount>{count || 0}</StyledStatsCount>
            <StyledStatsHeading>{heading}</StyledStatsHeading>
          </StyledStatsCounter>
        </StyledStatsSection>
      </Link>
    );
  };

  return (
    <StyledContentContainer>
      {appData?.bannerImages?.length > 0 && (
        <>
          <Slider images={appData.bannerImages.filter((i) => i.type === "Dashboard")} />
        </>
      )}

      {stats && (
        <>
          <StyledDashboardContainer>
            <StyledHeading>Support</StyledHeading>
            <StyledStatsContainerOuter>
              <StyledStatsContainer>
                {renderCard(
                  faTelegramPlane,
                  null,
                  "Telegram Bot",
                  null,
                  99,
                  "/telegrambot.jpeg"
                )}

                {supportNumber1 && supportNumber1 !== "0" && (
                  renderCard(
                    faWhatsapp,
                    truncate91(supportNumber1),
                    "Support Number 1",
                    `https://wa.me/91${truncate91(supportNumber1)}`,
                    1
                  )
                )}

                {supportNumber2 && supportNumber2 !== "0" && (
                  renderCard(
                    faWhatsapp,
                    truncate91(supportNumber2),
                    "Support Number 2",
                    `https://wa.me/91${truncate91(supportNumber2)}`,
                    2
                  )
                )}
              </StyledStatsContainer>
            </StyledStatsContainerOuter>

            <StyledHeading>Sales Summary</StyledHeading>
            <StyledStatsContainerOuter>
              <StyledStatsContainer>
                {renderCard(faCheck, stats.salesSuccessRecharges, "Success Recharges", "/seller/transactions", 0)}
                {renderCard(faXmark, stats.salesFailedRecharges, "Failed Recharges", "/seller/transactions", 1)}
                {renderCard(faExchange, stats.salesPendingRecharges, "Pending Recharges", "/seller/transactions-pending", 2)}
                {renderCard(faFlag, stats.salesPendingComplaints, "Complaint Recharges", "/seller/transactions-complaints", 3)}
                {renderCard(faBank, stats.todaysWalletRedeem, "Withdrawal Amount", "/payments/withdraw-funds", 1)}
              </StyledStatsContainer>
            </StyledStatsContainerOuter>

            <StyledHeading>Purchase Summary</StyledHeading>
            <StyledStatsContainerOuter>
              <StyledStatsContainer>
                {renderCard(faCheck, stats.purchaseSuccessRecharges, "Success Recharges", "/buyer/transactions", 0)}
                {renderCard(faXmark, stats.purchaseFailedRecharges, "Failed Recharges", "/buyer/transactions", 1)}
                {renderCard(faExchange, stats.purchasePendingRecharges, "Pending Recharges", "/buyer/transactions-pending", 2)}
                {renderCard(faFlag, stats.purchasePendingComplaints, "Complaint Recharges", "/buyer/transactions-complaints", 3)}
                {renderCard(faWallet, stats.todaysWalletTopup, "Wallet Topup Amount", "/payments/wallet-topups", 0)}
              </StyledStatsContainer>
            </StyledStatsContainerOuter>
          </StyledDashboardContainer>

          <StyledFlexBoxesContainer>
            {/* Sale Summary Table */}
            <StyledTableContainer>
              <StyledHeading>Sale Summary</StyledHeading>
              <StyledTableContainerIn>
                <TableBuilder
                  hasPagination={false}
                  isLoading={saleIsLoading}
                  tableHeadings={[
                    {
                      title: "Operator",
                      dataSelector: "operatorName",
                      cellrenderer: (val, item) => (
                        <StyledIconedText>
                          <Image source={item.operatorsLogo || "/no-image.png"} style={{ width: 50, height: 50 }} />
                          {val}
                        </StyledIconedText>
                      ),
                    },
                    {
                      title: "Amount",
                      dataSelector: "successAmount",
                      cellrenderer: (val, item) => `${formatCurrency(val)} (${item.successCount})`,
                    },
                    {
                      title: "Commission",
                      dataSelector: "commission",
                      cellrenderer: (val) => formatCurrency4Decimal(val),
                    },
                    { title: "Avg Margin", dataSelector: "averageMargin" },
                  ]}
                  tableData={saleSummary?.data?.slice(0, -1)}
                  tableFooter={[
                    { value: saleSummary?.data?.slice(-1)[0]?.operatorName },
                    { value: formatCurrency(saleSummary?.data?.slice(-1)[0]?.successAmount || 0) },
                    { value: formatCurrency4Decimal(saleSummary?.data?.slice(-1)[0]?.commission || 0) },
                    { value: saleSummary?.data?.slice(-1)[0]?.averageMargin },
                  ]}
                />
              </StyledTableContainerIn>
            </StyledTableContainer>

            {/* Purchase Summary Table */}
            <StyledTableContainer>
              <StyledHeading>Purchase Summary</StyledHeading>
              <StyledTableContainerIn>
                <TableBuilder
                  hasPagination={false}
                  isLoading={purchaseIsLoading}
                  tableHeadings={[
                    {
                      title: "Operator",
                      dataSelector: "operatorName",
                      cellrenderer: (val, item) => (
                        <StyledIconedText>
                          <Image source={item.operatorsLogo || "/no-image.png"} style={{ width: 50, height: 50 }} />
                          {val}
                        </StyledIconedText>
                      ),
                    },
                    {
                      title: "Amount",
                      dataSelector: "successAmount",
                      cellrenderer: (val, item) => `${formatCurrency(val)} (${item.successCount})`,
                    },
                    {
                      title: "Commission",
                      dataSelector: "commission",
                      cellrenderer: (val) => formatCurrency4Decimal(val),
                    },
                    { title: "Avg Margin", dataSelector: "averageMargin" },
                  ]}
                  tableData={purchaseSummary?.data?.slice(0, -1)}
                  tableFooter={[
                    { value: purchaseSummary?.data?.slice(-1)[0]?.operatorName },
                    { value: formatCurrency(purchaseSummary?.data?.slice(-1)[0]?.successAmount || 0) },
                    { value: formatCurrency4Decimal(purchaseSummary?.data?.slice(-1)[0]?.commission || 0) },
                    { value: purchaseSummary?.data?.slice(-1)[0]?.averageMargin },
                  ]}
                />
              </StyledTableContainerIn>
            </StyledTableContainer>
          </StyledFlexBoxesContainer>
        </>
      )}

      {/* QR Modal */}
      {isQRVisible && (
        <ModalOverlay onClick={() => setQRVisible(false)}>
          <ModalContent>
            <h3>Scan Telegram Bot QR</h3>
            <Image source={qrImage} style={{ width: "100%", maxWidth: 300 }} />
          </ModalContent>
        </ModalOverlay>
      )}
    </StyledContentContainer>
  );
};

export default withAppHOC(Dashboard);
